import { Fragment, useState } from 'react';
import {
  Show,
  useRecordContext,
  useCreatePath,
  RaRecord,
} from 'react-admin';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { SectionCard, Row, fmtCalendarDate } from '../../shared/Panel';
import { dateTimeNoSeconds, dateTimeRange } from '../../shared/dateTime';
import { extractionStatusLabel, preOrderStatusLabel, viaWriteStatusLabel } from '../../shared/status';
import { ClientFromPreOrder, windowText } from './bankRowText';
import { statusSinceText } from './statusSince';
import { ForkliftSection } from './ForkliftSection';
import { DateAnomalyWarning } from './DateAnomalyWarning';
import { OrderMarkerAlerts } from './OrderMarkerAlerts';
import { IntakeCancellationPanel } from './IntakeCancellationPanel';
import { TmsChangeAlerts } from './TmsChangeAlerts';
import { ReplanAlerts } from './ReplanAlerts';
import { UnassignRequestAlert } from './UnassignRequestAlert';
import { PreOrderLinkAlert } from './PreOrderLinkAlert';
import { ContractorLinkCheckAlert } from './ContractorLinkCheckAlert';
import { VatSection } from './VatSection';
import { RecordPriceStatistics } from './PriceStatisticsLine';
import { FEATURE_PRICE_STATISTICS } from './priceStatistics';
import { FeatureGate, useFeature } from '../featureFlags/FeatureGate';
import { ClientContactLog } from '../csWork/ClientContactLog';
import {
  OrderClientCommunication,
  OrderClientCommunicationCard,
} from '../clientCommunication/OrderClientCommunication';
import { FEATURE_CS_LIGHT } from '../csWork/csWorkApi';
import { OrderFuelSurcharge } from '../fuelSurcharges/FuelSurchargeSection';
import {
  planningStatusAppearance,
  isExpired,
  STATUS_HANDED_TO_TRADE,
} from './planningStatus';
import { OrderActions } from './OrderActions';
import { VehicleSuggestions } from './VehicleSuggestions';
import { VEHICLE_SUGGESTIONS_ANCHOR_ID } from './vehicleSuggestionsAnchor';
import { TradeCarriers } from './CarrierInquiries';
import { acceptanceStatusAppearance } from './acceptanceStatus';
import { ReleaseVariantButton } from './ReleaseVariantButton';
import { VariantValidityButton } from './VariantValidityButton';
import {
  conflictsText,
  timeConflictsOf,
  validityHint,
  validityText,
  variantValidityOf,
  type TimeConflict,
  type VariantValidity,
} from './variantValidity';
import { useVariantScope } from './variantScope';
import { ChangeHistory } from './ChangeHistory';
import { TransporeonSection } from '../transporeon/TransporeonSection';
import { ProcessLeadRows } from './processLead';
import { TradeTransferRow } from './tradeTransferInfo';
import { CarrierOffers } from './CarrierOffers';
import { useStatuses } from './statusDictionary';
import { SourceCell } from './cells';
import { ExchangePublications } from '../publications/ExchangePublications';
import { PermissionGate, useHasAnyPermission, useHasPermission } from '../PermissionGate';
import { ELEMENT } from '../roleCenter/elementKeys';
import { ModuleGate, companyIdOf, useModuleEnabled } from '../ModuleGate';
import type { ModuleKey } from '../modules';
import { tmsOrderStatusText } from './tmsOrderStatus';
import { pointTimeText } from './routePointTime';
import {
  hasCandidateDetails,
  originText,
  otherOrdersText,
  outOfListLabel,
  rankFor,
  rankText,
  shortListKey,
  useCandidateRanks,
  type CandidateRank,
} from './candidateRanks';
import {
  BusyMarker,
  CandidateCalcCells,
  CandidateCalcHeaders,
  DriverUnderVehicle,
  calcColumns,
  NOWRAP,
  VehicleModelLines,
} from './candidateCells';
import { BasisList, BasisToggle } from './SuggestionBasis';
import type { VehicleCandidate } from './suggestionsApi';
import { accessSourceNote, isMeasured } from './routeFigure';
import { TABLE_SCROLL_SX } from '../../shared/tableScroll';
import { bankRowWeightText, ldmText, vehicleTypeText } from './orderCargo';
import { GeoPointsInfo } from './geoPoint';
import { ReleaseReasonText, type ReleasedFor } from './ReleaseReason';
import { MISSING_FIELD_LABELS } from './preOrderFields';
import { FEATURE_ORDER_WINDOW } from './orderWindow/orderWindowFeature';
import { orderAdrText } from './orderAdr';
import { OrderWindowHeader, PreOrderBanner } from './orderWindow/OrderWindowHeader';
import { PreOrderStageSection } from './PreOrderStageSection';
import { FtlNotesSection } from '../ftlNotes/FtlNotesSection';
import { OrderChangesSection } from '../orderChanges/OrderChangesSection';
import {
  CargoSection,
  ClientSection,
  CommunicationSection,
  ExecutionEventsNote,
  InstructionsSection,
  OrderWorkSection,
  PriceSection,
  RouteSection,
} from './orderWindow/OrderWindowSections';
import { OrderTimeline } from './orderWindow/OrderTimeline';
import { OrderTodoPanel } from './orderWindow/OrderTodoPanel';
import { useNarrowLayout } from '../useNarrowLayout';

/**
 * Bank zleceń — szczegóły.
 *
 * Widok ma odpowiadać na pytanie „dlaczego to zlecenie wygląda tak, jak
 * wygląda", więc pokazuje **trzy osie osobno** (planowanie, akceptacja,
 * ryzyko), warianty razem z odrzuconymi i powodem odrzucenia, oraz kopertę
 * ekstrakcji, gdy rekord przyszedł z maila.
 *
 * Kandydatury zwolnione **zostają widoczne** — planista ma zobaczyć, kto
 * kandydował i dlaczego odpadł. Ukrycie ich zabrałoby podstawę do wyjaśnienia
 * decyzji, a to jest wprost wymóg z §13 rozdziału FTL.
 */

const CANDIDACY_ACTIVE = 'active';

/** Cudze zlecenie, do którego auto z wariantu też kandyduje (z API). */
type VariantConflict = { userOfferId: number; externalId: string | null };

/**
 * Boks „Stan”. „Ważne do” i „Ryzyko zgłoszone” tylko wtedy, gdy jest co
 * pokazać (omówienie 01.10.2026) — termin ważności ma głównie prezlecenie,
 * ryzyko mało które zlecenie, a dwa wiersze z myślnikiem na każdej karcie
 * wyglądały jak brak danych.
 */
export const Header = () => {
  const r = useRecordContext();
  const states = useStatuses();
  if (!r) return null;
  const appearance = planningStatusAppearance(r.planningStatus, states);
  const tms = acceptanceStatusAppearance(r.acceptanceStatus);
  return (
    <SectionCard title="Stan">
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
        <Chip size="small" color={appearance.color} label={appearance.label} />
        <Chip
          size="small"
          label={tms.label}
          sx={{ color: tms.textColor, backgroundColor: tms.backgroundColor }}
        />
        {r.atRisk ? (
          <Chip
            size="small"
            color="error"
            icon={<WarningAmberIcon />}
            label={(r.riskReason as string) || 'Zgłoszone ryzyko'}
          />
        ) : null}
        {isExpired(r.validUntil as string) ? (
          <Chip size="small" color="error" variant="outlined" label="Po terminie ważności" />
        ) : null}
      </Stack>
      <Table size="small" sx={{ mt: 1 }}>
        <TableBody>
          {r.validUntil ? (
            <Row label="Ważne do">{fmtCalendarDate(r.validUntil as string)}</Row>
          ) : null}
          {r.atRisk ? (
            <Row label="Ryzyko zgłoszone">{dateTimeNoSeconds(r.riskSince as string)}</Row>
          ) : null}
          {/* Dla „przekazane do Trade" to czas i autor przekazania (869f1fzha). */}
          <Row
            label={
              r.planningStatus === STATUS_HANDED_TO_TRADE
                ? 'Przekazane do Trade'
                : 'W tym stanie od'
            }
          >
            {statusSinceText(
              r.planningStatusSince,
              r.planningStatusBy as { username?: string | null } | null | undefined,
            )}
          </Row>
          <Row label="Status w TMS">{tmsOrderStatusText(r.tmsOrderStatus)}</Row>
          {/* Powód przekazania do Trade / zwrotu z listy (A6, 20.10.2026). */}
          <TradeTransferRow record={r} />
          {/* „Prowadzi” i „Realizacja wg TMS” (odpowiedzi 16.10.2026) —
              tylko gdy API niesie pola, czyli z przełącznikami. */}
          <ProcessLeadRows record={r} />
        </TableBody>
      </Table>
    </SectionCard>
  );
};

/** Wymagany typ pojazdu; przy wózku — chip obok, żeby nie zginął w napisie. */
const VehicleTypeRequirement = ({ value }: { value: unknown }) => {
  const { type, forklift } = vehicleTypeText(value);
  return (
    <>
      {type}
      {forklift && (
        <Chip size="small" color="warning" variant="outlined" label={forklift} sx={{ ml: 1 }} />
      )}
    </>
  );
};

const OrderBasics = () => {
  const r = useRecordContext();
  // Statystyka cen z VIA (13.10.2026) — wiersz tylko z przełącznikiem.
  const priceStatistics = useFeature(FEATURE_PRICE_STATISTICS);
  if (!r) return null;
  return (
    <SectionCard title="Zlecenie">
      <Table size="small">
        <TableBody>
          <Row label="Numer">{(r.externalId as string) ?? `#${r.id}`}</Row>
          <Row label="Źródło">
            <SourceCell record={r} />
          </Row>
          <Row label="Załadunek">
            {[r.fromCountry, r.fromPostalCode, r.fromCity].filter(Boolean).join(' ') || '—'}
          </Row>
          <Row label="Rozładunek">
            {[r.toCountry, r.toPostalCode, r.toCity].filter(Boolean).join(' ') || '—'}
          </Row>
          {/* Skąd punkt na mapie i czemu go nie ma (geokodowanie, 06.10.2026). */}
          <Row label="Punkt na mapie">
            <GeoPointsInfo record={r} />
          </Row>
          <Row label="Okno załadunku">
            {windowText(r.loadingDateFrom as string | null, r.loadingDateTo as string | null)}
          </Row>
          {/* Wymaganie zamówienia z TMS (`vehicle_type`), nie typ obsadzonego auta. */}
          <Row label="Wymagany typ pojazdu">
            <VehicleTypeRequirement value={r.assignedVehicleType} />
          </Row>
          <Row label="LDM">{ldmText(r.ldm)}</Row>
          {/* Wiersz prezlecenia nie ma punktów TMS — wtedy waga z prezlecenia (13.10.2026). */}
          <Row label="Waga">
            {bankRowWeightText(r.keyPoints, (r.preOrder as RaRecord | undefined)?.weight)}
          </Row>
          {/* ADR zlecenia z TMS (pole `adr`, odpowiedzi Marathonu/Euvic 16.10.2026, pyt. 52). */}
          <Row label="ADR">{orderAdrText(r)}</Row>
          <Row label="Dystans">
            {r.routeDistanceKm ? `${String(r.routeDistanceKm)} km` : '—'}
          </Row>
          <Row label="Stawka">
            {r.price ? `${String(r.price)} ${(r.currency as string) ?? ''}`.trim() : '—'}
          </Row>
          {priceStatistics && (
            <Row label="Cena klienta za km">
              <RecordPriceStatistics record={r} />
            </Row>
          )}
          <Row label="Kontrahent">
            {(r.contractor as RaRecord | undefined)?.legalName ?? <ClientFromPreOrder record={r} />}
          </Row>
          <Row label="Przewoźnik">
            {/* Wypełnione przy zleceniach oddanych na zewnątrz. Pusto znaczy
                „realizacja własna", a nie brak danych — dlatego piszemy to
                wprost zamiast myślnika. */}
            {(r.carrier as RaRecord | undefined)?.name ?? 'realizacja własna'}
          </Row>
          <Row label="Spółka">{(r.company as RaRecord | undefined)?.name ?? '—'}</Row>
          {/*
            Koperta zapisu do VIA tego samego transportu. Planista widzi
            stąd, czy zlecenie poszło już do systemu klienta — bez wchodzenia
            w prezlecenie i zgadywania, które zlecenie jest „to samo" (W4/W12).
          */}
          <Row label="Zapis do VIA">
            <ViaWriteLink record={r} />
          </Row>
        </TableBody>
      </Table>
    </SectionCard>
  );
};

/**
 * Odsyłacz do koperty zapisu do VIA.
 *
 * Brak koperty to normalny stan: zlecenie z importu z TMS nigdy nie miało
 * swojego pliku, bo powstało po stronie klienta. Piszemy to wprost, zamiast
 * pokazywać myślnik, który wygląda jak brak danych.
 */
export const ViaWriteLink = ({ record }: { record: RaRecord }) => {
  const createPath = useCreatePath();
  const envelope = record.order as RaRecord | undefined;
  if (!envelope) {
    return (
      <Typography variant="body2" color="text.secondary">
        brak — tego zlecenia nie zapisywano do VIA z aplikacji
      </Typography>
    );
  }
  return (
    <Link
      to={createPath({ resource: 'orders', type: 'show', id: envelope.id as number })}
    >
      {(envelope.orderNumber as string) ?? `#${envelope.id as number}`}
      {envelope.viaWriteStatus
        ? ` · ${viaWriteStatusLabel(String(envelope.viaWriteStatus))}`
        : ''}
    </Link>
  );
};

/** Punkty trasy w kolejności przejazdu — zła kolejność to zła trasa na ekranie. */
export const RoutePoints = () => {
  const r = useRecordContext();
  const points = (r?.keyPoints as RaRecord[] | undefined) ?? [];
  return (
    <SectionCard title={`Punkty trasy (${points.length})`}>
      {points.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Brak punktów — zlecenie nie pochodzi z TMS albo nie ma rozpisanej trasy.
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: 'text.secondary' }}>#</TableCell>
              <TableCell sx={{ color: 'text.secondary' }}>Typ</TableCell>
              <TableCell sx={{ color: 'text.secondary' }}>Miejsce</TableCell>
              <TableCell sx={{ color: 'text.secondary' }}>Termin (plan TMS)</TableCell>
              <TableCell sx={{ color: 'text.secondary' }}>Waga</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {points.map((p) => (
              <TableRow key={p.id as number}>
                <TableCell sx={{ border: 0 }}>{String(p.pointOrder ?? '—')}</TableCell>
                <TableCell sx={{ border: 0 }}>
                  {p.type === 'loading' ? 'Załadunek' : p.type === 'unloading' ? 'Rozładunek' : (p.type as string) ?? '—'}
                </TableCell>
                <TableCell sx={{ border: 0 }}>
                  {[p.countryCode, p.zipCode, p.city].filter(Boolean).join(' ') || '—'}
                </TableCell>
                <TableCell sx={{ border: 0 }}>
                  {pointTimeText(p.routerPlannedFrom as string, p.routerPlannedTo as string)}
                </TableCell>
                <TableCell sx={{ border: 0 }}>
                  {p.loadingWeight ? `${String(p.loadingWeight)} kg` : '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {points.length > 0 && (
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>
          Okno czasowe klienta (wspólne dla wszystkich punktów):{' '}
          {dateTimeRange(
            points[0].customerPlannedFrom as string,
            points[0].customerPlannedTo as string,
          )}
          . Termin przy punkcie to plan z TMS; po obsadzeniu ustala go planista.
        </Typography>
      )}
    </SectionCard>
  );
};

/**
 * Warianty pojazdu — wstępne przypisania obok ostatecznego obsadzenia.
 *
 * Zwolnione zostają na liście, wyszarzone i z powodem. §12: finalizacja
 * jednego wariantu zwalnia pozostałe — planista ma widzieć, które to były.
 * Żywy wariant, którego auto kandyduje też gdzie indziej, dostaje dopisek
 * z numerami cudzych zleceń — konflikt do pokazania, nie blokada.
 *
 * Pozycja wśród rekomendacji (status 29.09.2026): miejsce auta na liście
 * podpowiedzi ułożonej po dolocie, liczone przy otwarciu karty — także dla
 * auta wpisanego ręcznie; auto spoza listy dostaje powód.
 *
 * Wyliczenia przy wariancie (06.10.2026, użytkownik: „bez sensu, że na
 * widoku wariantów pokazuje się tylko pozycja, zamiast szczegółowych
 * wyliczeń”): przy aktywnym wariancie i obsadzonym aucie te same komórki co
 * w „Rekomendowanych pojazdach” (`candidateCells.tsx`) — kierowca, dolot
 * z kosztem i marżą, „Wolny od”, zapas, rozładunek, uwagi i „Na jakiej
 * podstawie”. Liczby przychodzą z tego samego przeliczenia co pozycja
 * (`GET /user-offers/:id/candidate-ranks`); auto spoza listy ma je z
 * adnotacją, czemu nie byłoby rekomendowane, a auto bez pozycji — sam powód.
 * Zwolnione warianty to historia — bez wyliczeń. Starsze API bez wyliczeń
 * i odmowa dla zlecenia: tabela jak dotąd, bez tych kolumn.
 *
 * Trader (rola działowa) widzi warianty z wyliczeniami tylko do odczytu —
 * rozdział Trade: „Pokazuje rzeczywiste pojazdy wstępnie i finalnie
 * przypisane”; zwalnianie zostaje przy planowaniu FTL. Gość widzi same
 * przypisania, bez wyliczeń.
 *
 * Opiekun pojazdu (opiekun FTL z TMS) przy wariancie i przy obsadzeniu, dla
 * każdego, kto widzi sekcję (01.10.2026): Trade §10 krok 5 — „pojazdy
 * wstępnie i finalnie przypisane oraz osobę odpowiedzialną”, FTL §12 —
 * „widoczny właściciel”.
 *
 * Termin ważności i konflikt w czasie (makieta back office v2, 20.10.2026,
 * przełącznik `variantValidity` — API niesie `validity` i `timeConflicts`
 * tylko z nim): przy żywym wariancie „ważny do …”, „po terminie” albo
 * „wygasa o …” i inne zlecenia, w których to auto jest wariantem albo
 * obsadzeniem w tym samym czasie. „Zmień termin” — autor albo Team Leader
 * (`validity.canChange` z API). Po terminie nic nie zwalnia się samo.
 */
export const VariantsSection = () => {
  const r = useRecordContext();
  const canPlan = useHasPermission('FTL_PLANNING');
  // Planista zwalnia warianty tylko swoich aut (07.10.2026, `variantScope.ts`).
  const canManageVariant = useVariantScope();
  // Wyliczenia czyta planowanie FTL i Trade (API: oba uprawnienia).
  const canSeeDetails = useHasAnyPermission(['FTL_PLANNING', 'TRADE']);
  // „Na jakiej podstawie” — rozwinięte uzasadnienie jednego auta naraz.
  const [basisFor, setBasisFor] = useState<number | null>(null);
  const narrow = useNarrowLayout();
  const candidacies = (r?.candidates as RaRecord[] | undefined) ?? [];
  const assigned = r?.assignedCar as RaRecord | undefined;
  const assignedCarId = (r?.assignedCarId ?? assigned?.id ?? null) as number | null;
  const active = candidacies.filter((k) => k.status === CANDIDACY_ACTIVE);
  const ranks = useCandidateRanks(
    r?.id,
    canSeeDetails && (candidacies.length > 0 || assignedCarId !== null)
      ? shortListKey(candidacies, assignedCarId)
      : '',
  );
  const details = canSeeDetails && hasCandidateDetails(ranks);
  // Obsadzenie z importu TMS nie ma wariantu — z wyliczeniami dostaje
  // własny wiersz na górze tabeli.
  const assignedOnly =
    details &&
    assignedCarId !== null &&
    !candidacies.some((k) => Number(k.carId) === Number(assignedCarId));
  // Poniżej 1280 px kierowca pod pojazdem, jak w rekomendacjach (29.10.2026).
  const calcCols = calcColumns(!narrow);
  const columns = 2 + (details ? calcCols : 1);
  // Dolot z map HERE (06.10.2026) — jak w rekomendacjach: choć jeden z map,
  // to nagłówek bez „(szacunek)” i źródło w każdej komórce. Trasy zlecenia
  // ten odczyt nie niesie, więc dymek marży mówi tylko o dolocie.
  const rankedCandidates = details ? (ranks?.vehicles ?? []).map((v) => v.candidate?.accessRoute) : [];
  const measuredAccess = rankedCandidates.some((route) => isMeasured(route));
  const accessNote = accessSourceNote(rankedCandidates);
  const toggleBasis = (carId: number) => setBasisFor((p) => (p === carId ? null : carId));

  /** Wyliczenia albo powód ich braku — wspólne dla wariantu i obsadzenia. */
  const detailCells = (carId: number) => {
    const rank = rankFor(ranks, carId);
    if (rank?.candidate) {
      return (
        <CandidateCalcCells
          candidate={rank.candidate}
          cellSx={CELL_SX}
          showAccessSource={measuredAccess}
          driverColumn={!narrow}
        />
      );
    }
    return (
      <TableCell colSpan={calcCols} sx={{ ...CELL_SX, color: 'text.secondary' }}>
        <Typography variant="body2" color="text.secondary" data-testid={`variant-no-details-${carId}`}>
          {noDetailsText(rank)}
        </Typography>
      </TableCell>
    );
  };

  /** Kierowca pod pojazdem w wąskim oknie — gdy są wyliczenia. */
  const driverUnder = (carId: number) => {
    const candidate = rankFor(ranks, carId)?.candidate;
    return narrow && details && candidate ? <DriverUnderVehicle candidate={candidate} /> : null;
  };

  /** Wiersz „Na jakiej podstawie” pod autem — gdy rozwinięty. */
  const basisRow = (carId: number, plate: string) => {
    const basis = rankFor(ranks, carId)?.candidate?.basis;
    if (!details || basisFor !== carId || !basis || basis.length === 0) return null;
    return (
      <TableRow>
        <TableCell colSpan={columns} sx={{ bgcolor: 'action.hover' }}>
          <BasisList title={`Na jakiej podstawie liczymy ${plate}`} basis={basis} />
        </TableCell>
      </TableRow>
    );
  };

  const basisToggle = (carId: number) =>
    details && (rankFor(ranks, carId)?.candidate?.basis?.length ?? 0) > 0 ? (
      <BasisToggle open={basisFor === carId} onToggle={() => toggleBasis(carId)} />
    ) : null;

  // „Aktywne: 2 z 5” pasuje do każdej liczby — „1 aktywnych” nie (01.10.2026).
  // Kontrakt `data-testid` paczki (S-22.10-02): sekcja obsadzenia i wariantów.
  return (
    <Box data-testid="ftl-zlecenie-obsadzenie">
    <SectionCard title={`Warianty obsadzenia (aktywne: ${active.length} z ${candidacies.length})`}>
      {assigned ? (
        <Box sx={{ mb: 1 }}>
          <Chip
            size="small"
            color="success"
            label={`Obsadzone: ${(assigned.registrationNumber as string) ?? `#${assigned.id}`}`}
          />
          {assigned.ftlCaretaker ? (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              Opiekun pojazdu: {String(assigned.ftlCaretaker)}
            </Typography>
          ) : null}
        </Box>
      ) : null}

      {candidacies.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: assignedOnly ? 1 : 0 }}>
          Brak wariantów — do zlecenia nie wskazano jeszcze wstępnie żadnego pojazdu.
        </Typography>
      ) : null}

      {candidacies.length > 0 || assignedOnly ? (
        // `overflowX` to tylko siatka bezpieczeństwa: kolumny są te same co
        // w rekomendacjach, a te mieszczą się przy 1366 px.
        <Box sx={TABLE_SCROLL_SX} data-testid="variants-scroll">
          <Table size="small" sx={VARIANTS_TABLE_SX}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: 'text.secondary' }}>Pojazd</TableCell>
                {details ? (
                  <CandidateCalcHeaders measuredAccess={measuredAccess} driverColumn={!narrow} />
                ) : (
                  <TableCell sx={{ color: 'text.secondary' }}>Powód zwolnienia</TableCell>
                )}
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {assignedOnly && assignedCarId !== null ? (
                <Fragment key="assigned">
                  <TableRow data-testid="variant-row-assigned">
                    <TableCell sx={CELL_SX}>
                      <VehicleCell
                        plate={(assigned?.registrationNumber as string) ?? `#${assignedCarId}`}
                        carId={assignedCarId}
                        state="assigned"
                        assignedHere
                        caretaker={(assigned?.ftlCaretaker as string | null | undefined) ?? null}
                        rank={rankFor(ranks, assignedCarId)}
                        total={ranks?.total ?? null}
                        showRank
                        origin={null}
                        addedBy={null}
                      />
                      {driverUnder(assignedCarId)}
                    </TableCell>
                    {detailCells(assignedCarId)}
                    <TableCell sx={CELL_SX} align="right">
                      {basisToggle(assignedCarId)}
                    </TableCell>
                  </TableRow>
                  {basisRow(assignedCarId, (assigned?.registrationNumber as string) ?? `#${assignedCarId}`)}
                </Fragment>
              ) : null}
              {candidacies.map((k) => {
                const isActive = k.status === CANDIDACY_ACTIVE;
                const carId = Number(k.carId);
                const plate = (k.car as RaRecord | undefined)?.registrationNumber ?? `#${String(k.carId)}`;
                return (
                  <Fragment key={k.id as number}>
                    <TableRow sx={{ opacity: isActive ? 1 : 0.55 }}>
                      <TableCell sx={CELL_SX}>
                        <VehicleCell
                          plate={plate}
                          carId={carId}
                          state={isActive ? 'active' : 'released'}
                          assignedHere={isActive && carId === Number(assignedCarId)}
                          caretaker={
                            ((k.car as RaRecord | undefined)?.ftlCaretaker as string | null | undefined) ?? null
                          }
                          rank={rankFor(ranks, carId)}
                          total={ranks?.total ?? null}
                          showRank={canSeeDetails}
                          origin={k.origin as string | null}
                          addedBy={(k.addedBy as RaRecord | undefined)?.username ?? null}
                          conflicts={isActive ? (k.candidateFor as VariantConflict[] | undefined) : undefined}
                          busy={isActive ? rankFor(ranks, carId)?.candidate?.busyWith : undefined}
                          validity={isActive && carId !== Number(assignedCarId) ? variantValidityOf(k) : null}
                          timeConflicts={isActive ? timeConflictsOf(k) : []}
                        />
                        {isActive ? driverUnder(carId) : null}
                      </TableCell>
                      {isActive && details ? (
                        detailCells(carId)
                      ) : (
                        // Zwolniony wariant to historia — bez wyliczeń, z powodem.
                        <TableCell
                          colSpan={details ? calcCols : 1}
                          sx={{ ...CELL_SX, color: 'text.secondary' }}
                        >
                          {k.releaseReason ? (
                            <>
                              {details ? 'Powód zwolnienia: ' : null}
                              <ReleaseReasonText
                                reason={String(k.releaseReason)}
                                releasedFor={k.releasedFor as ReleasedFor | null | undefined}
                              />
                            </>
                          ) : isActive ? (
                            ''
                          ) : (
                            '—'
                          )}
                        </TableCell>
                      )}
                      <TableCell sx={CELL_SX} align="right">
                        <Stack direction="column" spacing={0.5} sx={{ alignItems: 'flex-end' }}>
                          {/* Obsadzonego auta nie zwalniamy — odmawia tego serwer,
                              więc nie pokazujemy przycisku, który i tak zostanie
                              odrzucony. */}
                          {canPlan &&
                          isActive &&
                          carId !== Number(assignedCarId) &&
                          canManageVariant(
                            (k.car as RaRecord | undefined)?.ftlCaretaker as string | null | undefined,
                          ) &&
                          r ? (
                            <ReleaseVariantButton
                              userOfferId={r.id}
                              carId={k.carId as number}
                              registrationNumber={plate as string}
                            />
                          ) : null}
                          {/* Termin wariantu zmienia autor albo Team Leader (20.10.2026). */}
                          {isActive &&
                          carId !== Number(assignedCarId) &&
                          variantValidityOf(k)?.canChange &&
                          r ? (
                            <VariantValidityButton
                              userOfferId={r.id}
                              carId={carId}
                              registrationNumber={plate as string}
                              validUntil={variantValidityOf(k)?.validUntil ?? null}
                            />
                          ) : null}
                          {isActive ? basisToggle(carId) : null}
                        </Stack>
                      </TableCell>
                    </TableRow>
                    {isActive ? basisRow(carId, plate as string) : null}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      ) : null}
      {canSeeDetails && (candidacies.length > 0 || assignedOnly) ? (
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>
          {ranks?.refused
            ? `Dla tego zlecenia nie wyznaczamy rekomendacji, więc nie ma też pozycji. Powód: ${ranks.refused}.`
            : details
              ? `Wyliczenia są te same co w rekomendowanych pojazdach i liczone w tej chwili. ${
                  accessNote ? `${accessNote} Rozładunek to szacunek.` : 'Dolot i rozładunek to szacunek.'
                } Pozycja to miejsce pojazdu na liście rekomendacji uporządkowanej według dolotu. Nie jest to ocena pojazdu.`
              : 'Pozycja to miejsce pojazdu na liście rekomendacji uporządkowanej według dolotu, liczone w tej chwili. Nie jest to ocena pojazdu.'}
        </Typography>
      ) : null}
    </SectionCard>
    </Box>
  );
};

/** Komórki tabeli wariantów — bez dolnej kreski, jak dotąd. */
const CELL_SX = { border: 0 } as const;

/**
 * Węższe marginesy i wyrównanie do góry — jak w rekomendacjach: wiersz ma
 * kilka linii, a liczby wyśrodkowane w pionie rozjeżdżałyby się z numerem.
 */
const VARIANTS_TABLE_SX = {
  '& .MuiTableCell-root': { px: 1 },
  '& .MuiTableCell-body': { verticalAlign: 'top' },
  '& .MuiTableCell-head': { verticalAlign: 'bottom' },
} as const;

/** Najszerszy dopisek w kolumnie „Pojazd” (pozycja, powód spoza listy). */
const VEHICLE_NOTE_MAX_WIDTH = 220;

/** Dlaczego przy aktywnym wariancie nie ma liczb — powód z API albo brak danych. */
function noDetailsText(rank: CandidateRank | undefined): string {
  const reason = rank ? outOfListLabel(rank) : null;
  if (reason) return `Bez wyliczeń — ${reason}.`;
  return 'Bez wyliczeń — brak danych.';
}

type VehicleState = 'active' | 'released' | 'assigned';

const STATE_CHIPS: Record<VehicleState, { label: string; color: 'primary' | 'default' | 'success' }> = {
  // Rodzaj od „wariantu” (01.10.2026; wcześniej „Kandyduje” / „Zwolniona”).
  active: { label: 'Aktywny', color: 'primary' },
  released: { label: 'Zwolniony', color: 'default' },
  assigned: { label: 'Obsadzony', color: 'success' },
};

/**
 * Kolumna „Pojazd” wariantu: numer i stan, model i typ, opiekun, pozycja
 * wśród rekomendacji (z adnotacją, gdy liczby są mimo braku na liście),
 * skąd wariant i kto go dodał, konflikty z innymi zleceniami, zajętość.
 */
function VehicleCell({
  plate,
  carId,
  state,
  assignedHere = false,
  caretaker,
  rank,
  total,
  showRank,
  origin,
  addedBy,
  conflicts,
  busy,
  validity = null,
  timeConflicts = [],
}: {
  plate: string;
  carId: number;
  state: VehicleState;
  /** Auto obsadza zlecenie — dodatkowy znacznik obok stanu wariantu. */
  assignedHere?: boolean;
  caretaker: string | null;
  rank: CandidateRank | undefined;
  total: number | null;
  showRank: boolean;
  origin: string | null;
  addedBy: string | null;
  conflicts?: VariantConflict[];
  busy?: VehicleCandidate['busyWith'];
  /** Termin ważności (20.10.2026, `variantValidity`); `null` = bez pola. */
  validity?: VariantValidity | null;
  /** Konflikty w czasie z innymi zleceniami (20.10.2026). */
  timeConflicts?: TimeConflict[];
}) {
  const chip = STATE_CHIPS[state];
  const candidate = rank?.candidate ?? null;
  const caption = { display: 'block', maxWidth: VEHICLE_NOTE_MAX_WIDTH } as const;
  return (
    <>
      {/* Znaczniki mogą zejść pod numer — kolumna nie rozpycha tabeli. */}
      <Stack direction="row" spacing={0.5} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
        <Typography variant="body2" sx={{ fontWeight: 600, ...NOWRAP }}>
          {plate}
        </Typography>
        {state !== 'assigned' ? <Chip size="small" color={chip.color} label={chip.label} /> : null}
        {assignedHere ? <Chip size="small" color="success" label="Obsadzony" /> : null}
        {validity?.expired ? (
          <Chip size="small" color="error" variant="outlined" label="po terminie" data-testid={`variant-expired-${carId}`} />
        ) : null}
      </Stack>
      {validity ? (
        <Tooltip title={validityHint(validity)}>
          <Typography
            variant="caption"
            color={validity.expired ? 'error.main' : validity.expiresSoon ? 'warning.main' : 'text.secondary'}
            sx={caption}
            data-testid={`variant-validity-${carId}`}
          >
            {validityText(validity)}
          </Typography>
        </Tooltip>
      ) : null}
      {state === 'assigned' ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          bez wariantu (obsadzenie np. z TMS)
        </Typography>
      ) : null}
      {candidate ? (
        <VehicleModelLines makeAndModel={candidate.makeAndModel} carType={candidate.carType} />
      ) : null}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={caption}
        data-testid={`variant-caretaker-${carId}`}
      >
        opiekun: {caretaker ?? '—'}
      </Typography>
      {showRank ? (
        <Typography variant="caption" sx={caption} data-testid={`variant-rank-${carId}`}>
          {rankText(rank, total) ?? '—'}
        </Typography>
      ) : null}
      {/* Liczby mimo braku na liście — wariant dodany ręcznie spoza niej. */}
      {showRank && candidate && rank?.rank === null ? (
        <Typography variant="caption" color="warning.main" sx={caption} data-testid={`variant-not-recommended-${carId}`}>
          wyliczenia mimo to — tego pojazdu nie rekomendujemy
        </Typography>
      ) : null}
      {showRank && originText(origin) ? (
        <Typography variant="caption" color="text.secondary" sx={caption}>
          {originText(origin)}
        </Typography>
      ) : null}
      {addedBy ? (
        <Typography variant="caption" color="text.secondary" sx={caption}>
          dodał: {addedBy}
        </Typography>
      ) : null}
      {conflicts && conflicts.length > 0 ? (
        <Typography variant="caption" color="warning.main" sx={caption}>
          {otherOrdersText(conflicts)}
        </Typography>
      ) : null}
      {timeConflicts.length > 0 ? (
        <Tooltip title="Ten sam pojazd jest w nachodzącym czasie wariantem albo obsadzeniem innego zlecenia (szacowana zajętość). To ostrzeżenie, nie blokada — decyduje Team Leader.">
          <Typography
            variant="caption"
            color="warning.main"
            sx={{ ...caption, fontWeight: 600 }}
            data-testid={`variant-time-conflict-${carId}`}
          >
            {conflictsText(timeConflicts)}
          </Typography>
        </Tooltip>
      ) : null}
      {busy ? <BusyMarker busy={busy} /> : null}
    </>
  );
}

/**
 * Koperta ekstrakcji — tylko gdy rekord przyszedł z maila.
 *
 * To na niej opiera się stan „wymaga uzupełnienia", więc braki pokazujemy
 * wprost, a nie jako samą liczbę.
 */
export const PreOrderEnvelope = () => {
  const r = useRecordContext();
  const p = r?.preOrder as RaRecord | undefined;
  if (!p) return null;
  const missing = (p.missingFields as string[] | undefined) ?? [];
  const extraction = p.extractionStatus as string | null | undefined;
  const status = p.status as string | null | undefined;
  const dept = (p.responsibleDept as string | null | undefined) || null;
  return (
    <SectionCard title="Dane z maila (prezlecenie)">
      <Table size="small">
        <TableBody>
          <Row label="Stan odczytu z maila">
            {/* Kod w bazie, słowo na ekranie (`shared/status.ts`). */}
            {extraction ? extractionStatusLabel(extraction) : '—'}
          </Row>
          <Row label="Stan prezlecenia">{status ? preOrderStatusLabel(status) : '—'}</Row>
          {/* Dział odpowiedzialny z prezlecenia — wcześniej tylko na jego
              własnym ekranie (porównanie z makietą FTL, 13.10.2026). */}
          <Row label="Dział">{dept ?? '—'}</Row>
          <Row label="Brakujące pola">
            {missing.length === 0 ? (
              'Komplet'
            ) : (
              <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap' }}>
                {missing.map((b) => (
                  <Chip
                    key={b}
                    size="small"
                    color="warning"
                    variant="outlined"
                    label={MISSING_FIELD_LABELS[b] ?? b}
                  />
                ))}
              </Stack>
            )}
          </Row>
        </TableBody>
      </Table>
    </SectionCard>
  );
};

/** Alerty nad kartą — te same w obu układach karty. */
const OrderAlerts = () => (
  <>
    {/* Podejrzane daty z TMS — nad wszystkim, bo podważają resztę karty. */}
    <DateAnomalyWarning />
    {/* Anulowanie przez CS i Administrację (25.10.2026, za przełącznikiem). */}
    <IntakeCancellationPanel />
    {/* Zmiany w TMS po obsadzeniu — czekają na „Przyjmuję do wiadomości”. */}
    <TmsChangeAlerts />
    {/* Alert o przeplanowaniu — przegląd obsadzonych zleceń (status 29.09). */}
    <ReplanAlerts />
    {/* Wniosek o odpięcie pojazdu (omówienie 01.10.2026) — tylko z planowaniem FTL. */}
    <UnassignRequestAlert />
    {/* Jutro załadunek bez auta, rekomendacja sprzedaży (Marathon 07.10.2026). */}
    <OrderMarkerAlerts />
    {/* Zlecenie z TMS, które może pochodzić z prezlecenia (15.10.2026) — za przełącznikiem. */}
    <PreOrderLinkAlert />
    {/* Połączono z TMS mimo innego kontrahenta — do sprawdzenia (21.10.2026, za przełącznikiem). */}
    <ContractorLinkCheckAlert />
  </>
);

/**
 * Góra karty: alerty na całą szerokość, pod nimi dwie kolumny (od 1200 px).
 *
 * Omówienie 01.10.2026: po lewej „Stan” i zaraz pod nim „Zlecenie” — jedna
 * kolumna do czytania, po prawej „Akcje”. Wcześniej stan i akcje stały po
 * lewej, a dane zlecenia po prawej, więc czytało się na zmianę z dwóch
 * kolumn. Każda z kart sama zajmowała całą szerokość ekranu — przy 1920 px
 * pole „Powód ryzyka” miało 1500 px — stąd dwie kolumny. Na wąskim ekranie
 * kolumny idą jedna pod drugą: najpierw czytanie, potem akcje.
 */
export const OrderCardTop = () => (
  <>
    <OrderAlerts />
    <Box
      sx={{
        display: 'grid',
        gap: 2,
        gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'repeat(2, minmax(0, 1fr))' },
        alignItems: 'start',
      }}
    >
      <Stack spacing={2} sx={{ minWidth: 0 }} data-testid="order-card-reading">
        <Header />
        <OrderBasics />
        <ForkliftSection />
      </Stack>
      <Box sx={{ minWidth: 0 }} data-testid="order-card-actions">
        <OrderActions />
      </Box>
    </Box>
  </>
);

/**
 * Sekcja modułu spółki ZLECENIA (T.01, 06.10.2026): moduł wyłączony spółce
 * — sekcji nie ma, jak przy braku uprawnienia. API i tak odpowiada 403.
 */
const OrderModuleGate = ({ module, children }: { module: ModuleKey; children: ReactNode }) => {
  const record = useRecordContext<RaRecord>();
  return (
    <ModuleGate module={module} companyId={companyIdOf(record)}>
      {children}
    </ModuleGate>
  );
};

/** Przewoźnicy z historią i zapytania — każde za swoim modułem spółki zlecenia. */
const TradeCarriersSection = () => {
  const companyId = companyIdOf(useRecordContext<RaRecord>());
  const inquiriesEnabled = useModuleEnabled('carrierInquiries', companyId);
  return (
    <ModuleGate module="carriers" companyId={companyId}>
      <TradeCarriers inquiriesEnabled={inquiriesEnabled} />
    </ModuleGate>
  );
};

/**
 * Karta zlecenia. Niżej, na całą szerokość, idą tabele (podpowiedzi,
 * przewoźnicy, publikacje), którym ta szerokość jest potrzebna.
 *
 * Z przełącznikiem `orderWindowLayout` (Etap 1 makiety FTL, 15.10.2026) —
 * okno zlecenia według makiety (`OrderWindowLayout` niżej); bez niego karta
 * dokładnie jak dotąd.
 */
export const UserOfferShow = () => (
  <Show title="Zlecenie">
    <OrderCardBody />
  </Show>
);

const OrderCardBody = () =>
  useFeature(FEATURE_ORDER_WINDOW) ? <OrderWindowLayout /> : <CurrentOrderCard />;

/** Sekcja rekomendowanych pojazdów z kotwicą — ta sama w obu układach karty. */
const VehicleSuggestionsSection = () => (
  <PermissionGate permission="FTL_PLANNING" element={ELEMENT.orderVehicleSuggestions}>
    {/* Kotwica dla „Pokaż rekomendowane pojazdy” z alertu o przeplanowaniu
        (`vehicleSuggestionsAnchor.ts`); margines, żeby nagłówek sekcji
        nie chował się pod paskiem górnym. */}
    <OrderModuleGate module="vehicleSuggestions">
      {/* Kontrakt `data-testid` paczki (S-22.10-02): sekcja rekomendacji. */}
      <Box id={VEHICLE_SUGGESTIONS_ANCHOR_ID} sx={{ scrollMarginTop: '64px' }} data-testid="ftl-zlecenie-rekomendacje">
        <VehicleSuggestions />
      </Box>
    </OrderModuleGate>
  </PermissionGate>
);

/**
 * Status publikacji widzi też FTL — rozdział FTL §5: „status publikacji
 * i sprzedaży ma być widoczny bez pytania innego działu” (01.10.2026).
 * Przyciski giełd zostają przy Trade (`ExchangePublications`).
 */
const PublicationsSection = () => (
  <PermissionGate permission={['FTL_PLANNING', 'TRADE']} element={ELEMENT.orderPublications}>
    <OrderModuleGate module="exchanges">
      <ExchangePublications />
    </OrderModuleGate>
  </PermissionGate>
);

/**
 * Okno zlecenia według makiety FTL p. Mariusza (pkt 12 i 16; Etap 1, decyzja
 * Bartka 15.10.2026): nagłówek z numerem, relacją i liczbą punktów, pasek
 * etapów i podsumowania, baner prezlecenia, alerty, sekcje 1–7 i historia
 * zmian na dole. Sekcje 1–5 do czytania (dwie kolumny od 1200 px), sekcja 6
 * „Realizacja” robocza — oś czasu, warianty, rekomendacje (panel bez zmian,
 * tylko inne miejsce), przewoźnicy i giełdy, akcje. Treść kart i ich
 * uprawnienia bez zmian — to inny układ, nie nowe reguły.
 *
 * Panel „Do zrobienia” i czynności po obsadzeniu — od 24.10.2026 za
 * przełącznikiem `orderChecklist` (`orderWindow/OrderTodoPanel.tsx`), pod
 * alertami. Czego nie ma, choć makieta to ma (czeka na decyzje
 * p. Mariusza): punktu odcięcia i zdarzeń z realizacji wpisywanych ręcznie.
 */
const OrderWindowLayout = () => {
  const r = useRecordContext();
  return (
    <Stack spacing={2} sx={{ p: 2 }} data-testid="order-window">
      <OrderWindowHeader />
      {r ? <PreOrderBanner record={r} /> : null}
      {/* Etap prezlecenia (makieta back office v2, 20.10.2026) — tylko do
          odczytu; bez przełącznika API pola nie niesie i sekcji nie ma. */}
      <PreOrderStageSection />
      <OrderAlerts />
      {/* „Do zrobienia” i czynności po obsadzeniu (24.10.2026, `orderChecklist`). */}
      <OrderTodoPanel />
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'repeat(2, minmax(0, 1fr))' },
          alignItems: 'start',
          '& > *': { minWidth: 0 },
        }}
      >
        {/* W kolejności z makiety — na wąskim ekranie jedna pod drugą 1…5. */}
        <ClientSection
          viaWrite={r ? <ViaWriteLink record={r} /> : null}
          preOrderEnvelope={<PreOrderEnvelope />}
        />
        <RouteSection routePoints={<RoutePoints />} />
        <CargoSection forklift={<ForkliftSection />} />
        <PriceSection
          vat={
            <>
              <VatSection />
              {/* Korekta paliwowa (B4, 21.10.2026) — informacja, bez wpływu na marżę. */}
              <OrderFuelSurcharge />
            </>
          }
        />
        <InstructionsSection />
      </Box>
      <OrderWorkSection no={6} title="Realizacja">
        <OrderTimeline />
        <VariantsSection />
        <VehicleSuggestionsSection />
        <PermissionGate permission="TRADE" element={ELEMENT.orderTradeCarriers}>
          <TradeCarriersSection />
        </PermissionGate>
        {/* Rejestr ofert przewoźników (A9, 20.10.2026) — za przełącznikiem. */}
        <CarrierOffers />
        <PublicationsSection />
        <OrderActions />
        {/* „Uwagi dla CS” (makieta back office v2, 20.10.2026, `ftlNotes`). */}
        <FtlNotesSection />
        {/* Zmiany zlecenia „było → ma być” (fala B, B9, 21.10.2026,
            `orderChangeRegister`). */}
        <OrderChangesSection />
        <ExecutionEventsNote />
        {/* Dane z Transporeonu (04.11.2026, `transporeonImport`) — tylko odczyt. */}
        <TransporeonSection />
      </OrderWorkSection>
      <CommunicationSection />
      <ChangeHistory />
    </Stack>
  );
};

/** Karta jak dotąd (wersja „obecna”, jak na produkcji). */
const CurrentOrderCard = () => (
  <Stack spacing={2} sx={{ p: 2 }}>
    <OrderCardTop />
    {/* Etap prezlecenia i „Uwagi dla CS” (makieta back office v2, 20.10.2026)
        — za przełącznikami; wyłączone: karta jak dotąd. */}
    <PreOrderStageSection />
    <FtlNotesSection />
    {/* Zmiany zlecenia „było → ma być” (fala B, B9) — za przełącznikiem. */}
    <OrderChangesSection />
    {/* Awizacje i notatki CS (fala C, 21.10.2026) — za przełącznikiem `csLight`;
        w oknie zlecenia to sekcja 7. */}
    <FeatureGate feature={FEATURE_CS_LIGHT}>
      <SectionCard title="Komunikacja z klientem">
        <ClientContactLog />
        {/* Awizacje i aktualizacje dla klienta (26.10.2026, pakiet P1). */}
        <OrderClientCommunication />
      </SectionCard>
    </FeatureGate>
    <OrderClientCommunicationCard />
    {/* Sekcje działów (01.10.2026): bez uprawnienia sekcji nie ma wcale. */}
    <VehicleSuggestionsSection />
    <PermissionGate permission="TRADE" element={ELEMENT.orderTradeCarriers}>
      <TradeCarriersSection />
    </PermissionGate>
    {/* Rejestr ofert przewoźników (A9, 20.10.2026) — za przełącznikiem. */}
    <CarrierOffers />
    <PublicationsSection />
    <VatSection />
    {/* Korekta paliwowa (makieta back office v2, B4, 21.10.2026) — za
        przełącznikiem `fuelSurcharges`; tylko informacja, marża bez zmian. */}
    <OrderFuelSurcharge />
    <RoutePoints />
    {/* Dane z Transporeonu (04.11.2026, `transporeonImport`) — tylko odczyt. */}
    <TransporeonSection />
    <VariantsSection />
    <ChangeHistory />
    <PreOrderEnvelope />
  </Stack>
);
